import { supabase } from '@/integrations/supabase/client';
import { traceAsync, setSpanAttribute, getTraceHeaders } from './telemetry';
import { logger, getCorrelationHeaders } from './logger';

/**
 * Traced wrapper for Supabase operations
 * Provides helper functions to add OpenTelemetry spans to database operations
 * 
 * Usage: Instead of wrapping the client, use these helper functions
 * or wrap your queries manually with traceAsync()
 */

/**
 * Helper to trace edge function calls with proper context propagation
 * Automatically adds correlation ID and trace context headers
 */
export async function invokeTracedFunction<T = any>(
  functionName: string,
  payload?: Record<string, any>,
  options?: { timeout?: number }
): Promise<{ data: T | null; error: any }> {
  logger.info(`Invoking edge function: ${functionName}`, { functionName });
  
  return traceAsync(
    `edge-function.${functionName}`,
    async () => {
      const correlationHeaders = getCorrelationHeaders();
      const traceHeaders = getTraceHeaders();
      
      // Criar AbortController para timeout customizado
      const controller = new AbortController();
      const timeoutId = options?.timeout 
        ? setTimeout(() => controller.abort(), options.timeout)
        : null;
      
      try {
        const result = await supabase.functions.invoke<T>(functionName, {
          body: payload,
          headers: {
            ...correlationHeaders,
            ...traceHeaders,
          },
          signal: controller.signal as any,
        });
        
        if (timeoutId) clearTimeout(timeoutId);
        
        setSpanAttribute('function.name', functionName);
        setSpanAttribute('correlation.id', logger.getCorrelationId());
        
        if (result.error) {
          setSpanAttribute('function.error', true);
          setSpanAttribute('function.error.message', result.error.message);
          logger.error(`Edge function ${functionName} failed`, result.error, { functionName });
        } else {
          setSpanAttribute('function.success', true);
          logger.info(`Edge function ${functionName} succeeded`, { functionName });
        }
        
        return result;
      } catch (error: any) {
        if (timeoutId) clearTimeout(timeoutId);
        
        if (error.name === 'AbortError') {
          logger.error(`Edge function ${functionName} timed out`, error, { functionName });
          return { data: null, error: { message: 'Request timeout' } };
        }
        
        throw error;
      }
    },
    {
      'faas.trigger': 'http',
      'faas.name': functionName,
    }
  );
}

/**
 * Helper to trace database queries with structured logging
 * 
 * Example:
 * const result = await traceQuery('select', 'profiles', async () => {
 *   return supabase.from('profiles').select('*').eq('id', userId);
 * });
 */
export async function traceQuery<T>(
  operation: 'select' | 'insert' | 'update' | 'delete' | 'upsert',
  table: string,
  queryFn: () => Promise<any>
): Promise<T> {
  logger.debug(`DB query: ${operation} on ${table}`, { operation, table });
  
  return traceAsync(
    `db.${operation}.${table}`,
    async () => {
      const result = await queryFn();
      
      setSpanAttribute('db.system', 'postgresql');
      setSpanAttribute('db.table', table);
      setSpanAttribute('db.operation', operation);
      setSpanAttribute('correlation.id', logger.getCorrelationId());
      
      if (result.error) {
        setSpanAttribute('db.error', true);
        setSpanAttribute('db.error.message', result.error.message);
        logger.error(`DB ${operation} on ${table} failed`, result.error, { operation, table });
      } else {
        setSpanAttribute('db.success', true);
        if (Array.isArray(result.data)) {
          setSpanAttribute('db.rows.count', result.data.length);
          logger.debug(`DB ${operation} on ${table} returned ${result.data.length} rows`, { 
            operation, 
            table, 
            rowCount: result.data.length 
          });
        }
      }
      
      return result;
    },
    {
      'db.system': 'postgresql',
      'db.table': table,
      'db.operation': operation,
    }
  ) as Promise<T>;
}

/**
 * Export the regular supabase client
 * Use traceQuery() or traceAsync() to manually add tracing
 */
export { supabase };
